import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  Paper,
  Divider,
  ListItemIcon,
  ListItemText,
  LinearProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { getReportsV2 } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { Empty } from 'antd';
import MUIDataTable from 'mui-datatables';
import { reportStatuses, reportTypes, userCats } from '../../../../helpers/appstate';
import { Link } from 'react-router-dom';
import {
  AttachEmailOutlined,
  CardMembershipOutlined,
  Circle,
  KeyboardArrowRight,
  LockOpenOutlined,
  LockOutlined,
  MoreHoriz,
  PieChartOutline,
  HistoryOutlined,
  AssignmentTurnedInOutlined,
} from '@mui/icons-material';
import AppPagination from '../../../common/AppPagination';
import ResultLock from '../result-lock/ResultLock';
import GenerateCertificate from '../../facilities2/facility-quarters/GenerateCertificate';
import SendCertificate from '../../facilities2/facility-quarters/SendCertificate';

export default function AllResultNormal({
  props,
  history,
  reportYear,
  reportType,
  reportSearch,
  reportStatus,
  facilityId,
  stateId,
  reportQuarter,
  plannedStatus,
}) {
  const { userCategory, userPermission, userId } = props;
  const userPerm = userPermission?.map((mp) => mp.trim().toLowerCase()) || [];
  const canLockUnlockReport = userPerm.includes('lock_report');

  const pageSize = 10;
  const [pageNum, setPageNum] = useState(1);
  const [total, setTotal] = useState(0);
  const [resultList, setResultList] = useState([]);
  const [resultLoading, setResultLoading] = useState(false);
  const [resultSelected, setResultSelected] = useState();
  const [anchorOverflowMenuEl, setAnchorOverflowMenuEl] = useState(null);
  const [openResultLocker, setOpenResultLocker] = useState(false);
  const [openGenCert, setOpenGenCert] = useState(false);
  const [openSendCert, setOpenSendCert] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);

  console.log('resultList', resultList);

  useEffect(() => {
    fetchResultList();
  }, [
    reportYear,
    reportType,
    reportSearch,
    reportStatus,
    facilityId,
    stateId,
    reportQuarter,
    plannedStatus,
    pageNum,
  ]);

  useEffect(() => {
    if (refreshPage) fetchResultList();
  }, [refreshPage]);

  const handleOverflowMenuClick = (event) => setAnchorOverflowMenuEl(event.currentTarget);
  const handleOverflowMenuClose = () => setAnchorOverflowMenuEl(null);

  const fetchResultList = async () => {
    setResultLoading(true);
    try {
      const res = await getReportsV2(
        reportSearch,
        facilityId,
        stateId,
        reportStatus,
        reportYear,
        reportType,
        plannedStatus,
        reportQuarter,
        pageNum,
        pageSize
      );
      const { data, status, totalCount } = res?.result;
      if (status?.code === '00') {
        setResultList(data);
        setTotal(totalCount);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({ type: 'error', message: 'Error fetching monitoring results' });
    } finally {
      setResultLoading(false);
      setRefreshPage(false);
    }
  };

  const renderContent = () => {
    const columns = [
      {
        name: 'facility',
        label: 'Facility Details',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Box sx={{}}>
              <Link
                to={`/facilities/${resultList[index].facilityId}/view`}
                style={{ textDecoration: 'none' }}
              >
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="primary"
                  sx={{ '&:hover': { textDecoration: 'underline' } }}
                >
                  {resultList[index].facility}
                </Typography>
              </Link>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ textTransform: 'capitalize', display: 'block' }}
              >
                {resultList[index].stateName?.toLowerCase()} • {resultList[index].sectorName}
              </Typography>
            </Box>
          ),
        },
      },
      {
        name: 'reportTitle',
        label: 'Monitoring Report',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const item = resultList[index];
            const progress = Math.round(Number(item.resultProgressPercentage || 0));

            // Determine colors based on progress level
            let progressColor = '#d32f2f'; // Red for low progress
            if (progress >= 35 && progress < 75) progressColor = '#ed6c02'; // Orange for mid progress
            if (progress >= 75) progressColor = '#2e7d32'; // Green for high progress

            return (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1, minWidth: 100 }}>
                <Box sx={{ width: '100%' }}>
                  {/* Report Title */}
                  <Typography
                    variant="body2"
                    fontWeight={item.reportTitle === 'No Report Created' ? 400 : 700}
                    color={
                      item.reportTitle === 'No Report Created' ? 'text.disabled' : 'text.primary'
                    }
                  >
                    {item.reportTitle}
                  </Typography>

                  {item.reportId ? (
                    <Stack spacing={1} sx={{ mt: 0.5 }}>
                      {/* Compact Linear Progress Bar */}
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: alpha(progressColor, 0.15),
                          '& .MuiLinearProgress-bar': {
                            bgcolor: progressColor,
                            borderRadius: 3,
                          },
                        }}
                      />

                      {/* Metric Labels */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 500 }}
                        >
                          Entries: {item.entriesCount}/{item.totalSamplePoints}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: progressColor, fontWeight: 700 }}
                        >
                          {progress}%
                        </Typography>
                      </Stack>
                    </Stack>
                  ) : (
                    // Fallback spacing for rows without active schedules
                    <Box sx={{ height: 21 }} />
                  )}
                </Box>
              </Stack>
            );
          },
        },
      },
      {
        name: 'plannedReportStatus',
        label: 'Schedule Status',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const planStatus = resultList[index].plannedReportStatus;

            let chipColor = '#757575'; // Default fallback (e.g., DRAFT or unmapped states)

            const statusLower = planStatus?.toLowerCase();

            if (statusLower === 'ongoing') chipColor = '#0288d1'; // Info Blue
            if (statusLower === 'planned') chipColor = '#ed6c02'; // Warning Orange
            if (statusLower === 'not planned') chipColor = '#757575'; // Neutral Grey
            if (statusLower === 'submitted') chipColor = '#b100cd'; // Deep Purple
            if (statusLower === 'published') chipColor = '#2e7d32'; // Success Green
            if (statusLower === 'approved') chipColor = '#2e7d32'; // Success Green
            if (statusLower === 'rejected') chipColor = '#d32f2f'; // Error Red
            if (statusLower === 'planned & scheduled') chipColor = '#ed6c02';
            if (statusLower === 'approved but not published') chipColor = '#0288d1';

            return (
              <Chip
                label={planStatus || 'N/A'}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  bgcolor: alpha(chipColor, 0.1),
                  color: chipColor,
                  borderRadius: '6px',
                  textTransform: 'capitalize',
                }}
              />
            );
          },
        },
      },
      {
        name: 'reportType',
        label: 'Type',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const isQaqc = resultList[index].reportType === reportTypes.QAQC;
            return (
              <Typography variant="body2">{resultList[index].reportType}</Typography>
              // <Chip
              //   label={resultList[index].reportType}
              //   size="small"
              //   sx={{
              //     fontWeight: 700,
              //     fontSize: '0.65rem',
              //     bgcolor: isQaqc ? alpha('#ed6c02', 0.1) : alpha('#0288d1', 0.1),
              //     color: isQaqc ? '#ed6c02' : '#0288d1',
              //   }}
              // />
            );
          },
        },
      },
    ];

    if (userCategory === userCats.CONSULTANT || userCategory === userCats.EDM) {
      // columns.push({
      //   name: 'reportStatus',
      //   label: 'Status',
      //   options: {
      //     customHeadLabelRender: (a) => (
      //       <Typography
      //         variant="button"
      //         fontWeight={700}
      //         color="text.secondary"
      //         sx={{ fontSize: '0.75rem' }}
      //       >
      //         {a.label}
      //       </Typography>
      //     ),
      //     customBodyRenderLite: (index) => {
      //       const status = resultList[index].reportStatus;
      //       const hasStarted = Number(resultList[index]?.resultProgressPercentage) > 0;

      //       let statusColor = '#757575';
      //       if (status === reportStatuses.PUBLISHED) statusColor = '#2e7d32';
      //       if (status === reportStatuses.SUBMITTED) statusColor = '#1976d2';

      //       return (
      //         <Chip
      //           label={status ? status.toLowerCase() : 'uncreated'}
      //           size="small"
      //           icon={
      //             status === reportStatuses.DRAFT && hasStarted ? (
      //               <Circle color="primary" sx={{ fontSize: '10px !important' }} />
      //             ) : (
      //               undefined
      //             )
      //           }
      //           sx={{
      //             textTransform: 'capitalize',
      //             fontWeight: 700,
      //             bgcolor: alpha(statusColor, 0.1),
      //             color: statusColor,
      //             borderRadius: '6px',
      //           }}
      //         />
      //       );
      //     },
      //   },
      // });

      columns.push({
        name: '',
        label: 'Action',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
              align="center"
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const i = resultList[index];
            return (
              <Box align="center">
                {/* <Button
                  variant="text"
                  size="small"
                  endIcon={<KeyboardArrowRight />}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                  onClick={() => {
                    if (i.reportId) {
                      history.push(
                        `results/view/${i?.facilityId}/${i?.reportId}/${i?.reportQuarter}/${i?.reportYear}`
                      );
                    } else {
                      openNotification({
                        type: 'info',
                        message: 'Please generate a schedule first.',
                      });
                    }
                  }}
                >
                  {i.action || 'View'}
                </Button> */}
                {userCategory === userCats.EDM && i.reportId && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      handleOverflowMenuClick(e);
                      setResultSelected(i);
                    }}
                  >
                    <MoreHoriz />
                  </IconButton>
                )}
              </Box>
            );
          },
        },
      });
    }

    if (userCategory === userCats.PERSONNEL || userCategory === userCats.REGULATOR) {
      columns.push({
        name: '',
        label: '',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const i = resultList[index];
            return i?.reportStatus === reportStatuses.PUBLISHED ? (
              <Button
                variant="contained"
                size="small"
                disableElevation
                endIcon={<KeyboardArrowRight />}
                sx={{ borderRadius: '8px', textTransform: 'none' }}
                onClick={() =>
                  history.push(
                    `results/view/${i?.facilityId}/${i?.reportId}/${i?.reportQuarter}/${i?.reportYear}`
                  )
                }
              >
                View Result
              </Button>
            ) : (
              // <Typography variant="caption" color="textDisabled" fontWeight={600}>
              //   {i.reportId ? 'Pending Release' : 'No Action Required'}
              // </Typography>
              <>-</>
            );
          },
        },
      });
    }

    return (
      <MUIDataTable
        data={resultList}
        columns={columns}
        options={{
          elevation: 0,
          search: false,
          filter: false,
          download: false,
          print: false,
          viewColumns: false,
          selectableRows: 'none',
          responsive: 'standard',
          pagination: false,
          setRowProps: (ev, dataIndex) => ({
            style: { backgroundColor: dataIndex % 2 === 0 ? '#ffffff' : alpha('#f5f7ff', 0.5) },
          }),
          setTableProps: () => ({ size: 'small' }),
        }}
      />
    );
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {resultLoading ? (
          <Box sx={{ p: 4 }}>
            <LoaderSkeleton />
            <Divider sx={{ my: 2 }} />
            <LoaderSkeleton />
          </Box>
        ) : resultList?.length < 1 ? (
          <Box sx={{ py: 8 }}>
            <Empty description="No normal monitoring reports found" />
          </Box>
        ) : (
          renderContent()
        )}
      </Paper>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary" fontWeight={500}>
          Showing results {(pageNum - 1) * pageSize + 1} - {Math.min(pageNum * pageSize, total)} of{' '}
          {total}
        </Typography>
        <AppPagination
          defaultCurrent={1}
          defaultPageSize={pageSize}
          total={total}
          onPageChange={(p) => setPageNum(p)}
        />
      </Box>

      {/* Overflow Menu for EDM Actions */}
      <Menu
        anchorEl={anchorOverflowMenuEl}
        open={Boolean(anchorOverflowMenuEl)}
        onClose={handleOverflowMenuClose}
        PaperProps={{
          sx: { borderRadius: '12px', mt: 1, width: 220, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' },
        }}
      >
        <MenuItem
          onClick={() => {
            handleOverflowMenuClose();
            history.push(
              `results/view/${resultSelected?.facilityId}/${resultSelected?.reportId}/${resultSelected?.reportQuarter}/${resultSelected?.reportYear}`
            );
          }}
        >
          <ListItemIcon>
            <PieChartOutline fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="View Details"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          />
        </MenuItem>

        {canLockUnlockReport && (
          <MenuItem
            onClick={() => {
              setOpenResultLocker(true);
              handleOverflowMenuClose();
            }}
          >
            <ListItemIcon>
              {resultSelected?.isVisible ? (
                <LockOutlined fontSize="small" />
              ) : (
                <LockOpenOutlined fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={resultSelected?.isVisible ? 'Lock Result' : 'Unlock Result'}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            setOpenGenCert(true);
            handleOverflowMenuClose();
          }}
        >
          <ListItemIcon>
            <CardMembershipOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Generate Certificate"
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenSendCert(true);
            handleOverflowMenuClose();
          }}
        >
          <ListItemIcon>
            <AttachEmailOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Send Certificate" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
      </Menu>

      {/* Logic components */}
      {openResultLocker && (
        <ResultLock
          open={openResultLocker}
          onClose={() => setOpenResultLocker(false)}
          record={resultSelected}
          onRefreshPage={() => setRefreshPage(true)}
        />
      )}
      {openGenCert && (
        <GenerateCertificate
          open={openGenCert}
          onClose={() => {
            setOpenGenCert(false);
            setRefreshPage(true);
          }}
          title="Generate Certificate"
          report={resultSelected}
        />
      )}
      {openSendCert && (
        <SendCertificate
          open={openSendCert}
          onClose={() => {
            setOpenSendCert(false);
            setRefreshPage(true);
          }}
          title="Send Certificate"
          report={resultSelected}
        />
      )}
    </Box>
  );
}
